/**
 * Permission Service
 * Manages user permissions in Angular frontend
 * 
 * Architecture:
 * 1. Permissions are fetched from backend after login
 * 2. Stored in memory (BehaviorSubject) for reactive updates
 * 3. Provides hasPermission() method for permission checks
 * 4. Used by components and directives to control UI visibility
 * 
 * Security Note:
 * Frontend permission checks are ONLY for UX (hiding/showing buttons)
 * Backend ALWAYS validates permissions on every API call
 * Never rely on frontend checks for security
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Permission action type
 * Must match backend REPORT_PERMISSIONS constants
 */
export type PermissionAction =
  // Core Report Actions
  | 'REPORT_VIEW'
  | 'REPORT_CREATE'
  | 'REPORT_UPDATE'
  | 'REPORT_DELETE'
  // Report Data Actions
  | 'REPORT_EXPORT'
  | 'REPORT_SCHEDULE'
  | 'REPORT_REPROCESS'
  // Dashboard Actions
  | 'DASHBOARD_VIEW'
  | 'DASHBOARD_CREATE'
  | 'DASHBOARD_UPDATE'
  | 'DASHBOARD_DELETE'
  // Dashboard Group Actions
  | 'DASHBOARD_GROUP_VIEW'
  | 'DASHBOARD_GROUP_CREATE'
  | 'DASHBOARD_GROUP_UPDATE'
  | 'DASHBOARD_GROUP_DELETE'
  // Advanced Actions
  | 'REPORT_FORCE_UPDATE'
  | 'PERMISSION_MANAGE'
  | 'AUDIT_LOG_VIEW'
  // Job Management
  | 'JOB_VIEW'
  | 'JOB_RETRY'
  | 'JOB_CANCEL';

/**
 * User permissions response from backend
 */
export interface UserPermissions {
  roleId: number;
  roleName: string;
  permissions: PermissionAction[];
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private baseURL = environment.MICROSERVICE_URL;
  // private baseURL = 'https://tlu61ri450.execute-api.ap-south-1.amazonaws.com/api';
  
  /**
   * BehaviorSubject to store user permissions
   * Allows components to subscribe to permission changes
   */
  private permissionsSubject = new BehaviorSubject<PermissionAction[]>([]);
  
  /**
   * Observable stream of permissions
   * Components can subscribe to this to react to permission changes
   */
  public permissions$ = this.permissionsSubject.asObservable();
  
  /**
   * Current user's role information
   */
  private roleInfo: { roleId: number; roleName: string } | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Fetch user permissions from backend
   * Should be called after successful login
   * 
   * @returns Observable of user permissions
   * 
   * @example
   * // In login component after successful authentication
   * this.permissionService.loadPermissions().subscribe({
   *   next: (permissions) => {
   *     console.log('Loaded permissions:', permissions);
   *     // Navigate to dashboard
   *   },
   *   error: (error) => {
   *     console.error('Failed to load permissions:', error);
   *   }
   * });
   */

  loadPermissions(): Observable<ApiResponse<UserPermissions>> {
    return this.http.get<ApiResponse<UserPermissions>>(
      `${this.baseURL}/permissions/my-permissions`
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.setPermissions(response.data.permissions);
          this.roleInfo = {
            roleId: response.data.roleId,
            roleName: response.data.roleName
          };
        }
      })
    );
  }

  /**
   * Set permissions manually
   * Used after loading permissions from backend
   * 
   * @param permissions - Array of permission actions
   */
  setPermissions(permissions: PermissionAction[]): void {
    this.permissionsSubject.next(permissions);
  }

  /**
   * Get current permissions as array
   * 
   * @returns Array of permission actions
   */
  getPermissions(): PermissionAction[] {
    return this.permissionsSubject.value;
  }

  /**
   * Check if user has a specific permission
   * This is the main method used throughout the app
   * 
   * @param action - Permission action to check
   * @returns true if user has permission, false otherwise
   * 
   * @example
   * // In component
   * canCreateReport(): boolean {
   *   return this.permissionService.hasPermission('REPORT_CREATE');
   * }
   * 
   * @example
   * // In template
   * <button *ngIf="permissionService.hasPermission('REPORT_CREATE')">
   *   Create Report
   * </button>
   */
  hasPermission(action: PermissionAction): boolean {
    const permissions = this.permissionsSubject.value;
    return permissions.includes(action);
  }

  /**
   * Check if user has ANY of the specified permissions
   * User needs at least one of the permissions
   * 
   * @param actions - Array of permission actions
   * @returns true if user has any of the permissions
   * 
   * @example
   * canViewData(): boolean {
   *   return this.permissionService.hasAnyPermission([
   *     'REPORT_VIEW',
   *     'DASHBOARD_VIEW'
   *   ]);
   * }
   */
  hasAnyPermission(actions: PermissionAction[]): boolean {
    const permissions = this.permissionsSubject.value;
    return actions.some(action => permissions.includes(action));
  }

  /**
   * Check if user has ALL of the specified permissions
   * User needs all specified permissions
   * 
   * @param actions - Array of permission actions
   * @returns true if user has all permissions
   * 
   * @example
   * canExportScheduledReport(): boolean {
   *   return this.permissionService.hasAllPermissions([
   *     'REPORT_EXPORT',
   *     'REPORT_SCHEDULE'
   *   ]);
   * }
   */
  hasAllPermissions(actions: PermissionAction[]): boolean {
    const permissions = this.permissionsSubject.value;
    return actions.every(action => permissions.includes(action));
  }

  /**
   * Get current user's role information
   * 
   * @returns Role ID and name, or null if not loaded
   */
  getRoleInfo(): { roleId: number; roleName: string } | null {
    return this.roleInfo;
  }

  /**
   * Clear all permissions
   * Should be called on logout
   */
  clearPermissions(): void {
    this.permissionsSubject.next([]);
    this.roleInfo = null;
  }

  /**
   * Check if permissions are loaded
   * 
   * @returns true if permissions are loaded
   */
  isLoaded(): boolean {
    return this.permissionsSubject.value.length > 0;
  }

  // ========================================
  // ADMIN METHODS
  // For admin UI to manage role permissions
  // ========================================

  /**
   * Get permissions for all roles (admin only)
   * Used in admin UI to display permission matrix
   */
  getAllRolePermissions(): Observable<ApiResponse<UserPermissions[]>> {
    return this.http.get<ApiResponse<UserPermissions[]>>(
      `${this.baseURL}/permissions/all-roles`
    );
  }

  /**
   * Get permissions for a specific role (admin only)
   * 
   * @param roleId - Role ID to fetch permissions for
   */
  getPermissionsByRole(roleId: number): Observable<ApiResponse<UserPermissions>> {
    return this.http.get<ApiResponse<UserPermissions>>(
      `${this.baseURL}/permissions/role/${roleId}`
    );
  }

  /**
   * Bulk update permissions for a role (admin only)
   * This is the recommended way to update role permissions
   * 
   * @param roleId - Role ID to update
   * @param permissions - Array of permission actions to grant
   * 
   * @example
   * // In admin component
   * updateRolePermissions(roleId: number, selectedPermissions: PermissionAction[]): void {
   *   this.permissionService.bulkUpdatePermissions(roleId, selectedPermissions).subscribe({
   *     next: (response) => {
   *       this.toastr.success('Permissions updated successfully');
   *     },
   *     error: (error) => {
   *       this.toastr.error('Failed to update permissions');
   *     }
   *   });
   * }
   */
  bulkUpdatePermissions(
    roleId: number,
    permissions: PermissionAction[]
  ): Observable<ApiResponse<UserPermissions>> {
    return this.http.post<ApiResponse<UserPermissions>>(
      `${this.baseURL}/permissions/bulk-update`,
      { roleId, permissions }
    );
  }
}

